// src/components/appointments/UnpaidPaymentsList.js
import React, { useState } from 'react';
import { useUnpaidPayments } from '../../hooks/usePaymentStatus';
import PaymentStatusBadge from '../appointments/PaymentStatusBadge';

const UnpaidPaymentsList = ({
  onRequestPayment,
  onCancelPayment,
  onRefreshAppointment,
}) => {
  const { unpaidPayments, getCountByStatus, loading, error, deletePayment } = useUnpaidPayments();
  const counts = getCountByStatus();

  const handleDeletePayment = async (paymentId) => {
    try {
      const payment = unpaidPayments.find(p => p.id === paymentId);

      // Capture appointmentIds array before deletion (or fallback to single appointmentId for backward compatibility)
      const appointmentIds = payment?.appointmentIds || (payment?.appointmentId ? [payment.appointmentId] : []);

      // Delete the payment document
      await deletePayment(paymentId);

      // Refresh cache for all associated appointments
      if (appointmentIds && appointmentIds.length > 0) {
        // Refresh each appointment to invalidate cache
        for (const appointmentId of appointmentIds) {
          await onRefreshAppointment(appointmentId);
        }
      }

    } catch (error) {
      console.error('Error deleting payment:', error);
    }
  };

  if (loading) {
    return (
      <div className="bg-white/70 backdrop-blur-lg rounded-2xl border border-white/20 shadow-xl p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded-lg w-48"></div>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-16 bg-gray-200 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50/70 backdrop-blur-lg rounded-2xl border border-red-100/20 shadow-xl p-6">
        <div className="text-red-600 text-center">
          <div className="text-lg font-medium mb-2">שגיאה בטעינת התשלומים</div>
          <div className="text-sm">{error}</div>
        </div>
      </div>
    );
  }

  if (unpaidPayments.length === 0) {
    return (
      <div>
        <div className="bg-white/70 backdrop-blur-lg rounded-2xl border border-white/20 shadow-xl p-8">
          <div className="text-center text-gray-500">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="text-lg font-medium">אין תשלומים ממתינים</div>
          <div className="text-sm mt-1">כל התשלומים מעודכנים</div>
        </div>
      </div>
      </div>
    );
  }

  return (
    <div className="bg-white/70 backdrop-blur-lg rounded-2xl border border-white/20 shadow-xl">

      {/* Header with counts */}
      <div className="p-6 border-b border-gray-100/50">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-900">תשלומים ממתינים</h3>
          <div className="flex items-center gap-4">
            {counts.pending > 0 && (
              <div className="flex items-center gap-1 text-xs">
                <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                <span className="text-gray-600">{counts.pending} ממתין</span>
              </div>
            )}
            {counts.failed > 0 && (
              <div className="flex items-center gap-1 text-xs">
                <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                <span className="text-gray-600">{counts.failed} נכשל</span>
              </div>
            )}
            {counts.expired > 0 && (
              <div className="flex items-center gap-1 text-xs">
                <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                <span className="text-gray-600">{counts.expired} פג תוקף</span>
              </div>
            )}
            {counts.completed > 0 && (
              <div className="flex items-center gap-1 text-xs">
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                <span className="text-gray-600">{counts.completed} הושלם</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Payment List */}
      <div className="divide-y divide-gray-100/50 max-h-96 overflow-y-auto">
        {unpaidPayments.map((payment) => (
          payment.status !== 'cancelled' &&
          <UnpaidPaymentCard
            key={payment.id}
            payment={payment}
            onRequestPayment={onRequestPayment}
            onCancelPayment={onCancelPayment}
            onDeletePayment={handleDeletePayment}
          />
        ))}
      </div>
    </div>
  );
};

const UnpaidPaymentCard = ({ payment, onRequestPayment, onCancelPayment, onDeletePayment }) => {
  const [actionLoading, setActionLoading] = useState(null);

  const getUrgencyColor = (status, hoursElapsed) => {
    if (status === 'completed') return 'border-r-green-400 bg-green-50/30';
    if (status === 'pending' && hoursElapsed < 1) return 'border-r-yellow-400 bg-yellow-50/30';
    if (status === 'pending' && hoursElapsed < 24) return 'border-r-orange-400 bg-orange-50/30';
    if (status === 'failed') return 'border-r-red-400 bg-red-50/30';
    if (status === 'expired') return 'border-r-gray-400 bg-gray-50/30';
    return 'border-r-pink-400 bg-pink-50/30';
  };

  const canCreateNewLink = () => {
    return ['failed', 'expired'].includes(payment.status);
  };

  const canCancel = () => {
    return payment.status === 'pending';
  };

  const isCompleted = () => {
    return payment.status === 'completed';
  };

  const handleAction = async () => {
    if (canCancel() && onCancelPayment) {
      setActionLoading('cancel');
      try {
        await onCancelPayment(payment.id);
      } catch (error) {
        console.error('Error cancelling payment:', error);
      } finally {
        setActionLoading(null);
      }
    } else if (canCreateNewLink() && onRequestPayment) {
      setActionLoading('request');
      try {
        await onRequestPayment(payment);
      } catch (error) {
        console.error('Error requesting payment:', error);
      } finally {
        setActionLoading(null);
      }
    }
  };

  const handleDelete = async () => {
    if (!isCompleted() || !onDeletePayment) return;

    setActionLoading('delete');
    try {
      await onDeletePayment(payment.id);
    } catch (error) {
      console.error('Error deleting payment:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const getActionText = () => {
    if (actionLoading === 'cancel') return 'מבטל...';
    if (actionLoading === 'request') return 'שולח...';
    if (actionLoading === 'delete') return 'מוחק...';
    if (canCancel()) return 'בטל';
    if (canCreateNewLink()) return 'שלח שוב';
    return null;
  };

  const getActionStyle = () => {
    const baseDisabled = actionLoading ? 'opacity-50 cursor-not-allowed' : '';

    if (canCancel()) {
      return `bg-red-100 text-red-700 hover:bg-red-200 ${baseDisabled}`;
    }
    if (canCreateNewLink()) {
      return `bg-gradient-to-r from-pink-500 to-purple-600 text-white hover:from-pink-600 hover:to-purple-700 ${baseDisabled}`;
    }
    return 'bg-gray-100 text-gray-400 cursor-not-allowed';
  };

  const actionText = getActionText();

  return (
    <div className={`p-4 hover:bg-white/30 transition-all duration-200 border-r-4 ${getUrgencyColor(payment.status, payment.hoursElapsed)}`}>
      <div className="flex items-start justify-between gap-4">
        {/* Main Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1">
              <h3 className="font-medium text-gray-900 truncate">{payment.customerName}</h3>
              <p className="text-sm text-gray-600 truncate">{payment.serviceName}</p>
            </div>
            <div className="text-left mr-3">
              <div className="font-semibold text-gray-900">₪{payment.amount}</div>
              <div className="text-xs text-gray-500">{payment.timeDisplay}</div>
            </div>
          </div>

          {/* Status and Action Row */}
          <div className="flex items-center justify-between">
            <PaymentStatusBadge paymentStatus={payment.status} />

            {/* Action Button or Green Checkmark */}
            {isCompleted() && payment.invoice === true ? (
              <button
                onClick={handleDelete}
                disabled={actionLoading === 'delete'}
                className={`w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center hover:bg-green-600 transition-all duration-200 ${actionLoading === 'delete' ? 'opacity-50 cursor-not-allowed' : ''}`}
                title="סמן כנראה וחזור מהרשימה"
              >
                {actionLoading === 'delete' ? (
                  <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            ) : actionText && (
              <button
                onClick={handleAction}
                disabled={!!actionLoading}
                className={`px-3 py-1 text-xs font-medium rounded-lg transition-all duration-200 ${getActionStyle()}`}
              >
                {actionText}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnpaidPaymentsList;